import { useMutation } from '@tanstack/react-query';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';
import { queryClient } from '../../../modules/reactQuery/contexts/QueryClient/utils/queryClient';
import {
  getRachelChatFeedbackKey,
  getRachelChatKey,
  isRachelChatsListQueryKey,
} from './rachelChats.keys';
import {
  approveRachelAgentRun,
  createRachelChat,
  deleteRachelChat,
  renameRachelChat,
  setRachelChatToolApproval,
  setRachelMessageFeedback,
} from './rachelChats.repository';
import {
  ApproveRachelAgentRunParams,
  RachelChatOutput,
  RenameRachelChatParams,
  SetRachelChatToolApprovalParams,
  SetRachelMessageFeedbackParams,
} from './rachelChats.types';

export const useCreateRachelChat = () =>
  useMutation({
    mutationFn: async (): Promise<RachelChatOutput> => {
      const response = await createRachelChat();
      if (!response.ok || !response.data) {
        throw response;
      }
      return response.data;
    },
  });

export const useDeleteRachelChat = () =>
  useMutation({
    mutationFn: async (chatId: string) => {
      const response = await deleteRachelChat(chatId);
      if (!response.ok) {
        throw response;
      }
    },
  });

export const useRenameRachelChat = () => {
  const { team } = useApplicationContext();

  return useMutation({
    mutationFn: async (params: RenameRachelChatParams) => {
      const response = await renameRachelChat(params);
      if (!response.ok) {
        throw response;
      }
    },
    onSuccess: async (_data, { chatId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          predicate: (query) => isRachelChatsListQueryKey(query.queryKey),
        }),
        queryClient.invalidateQueries({
          queryKey: [team?.id, ...getRachelChatKey(chatId)],
        }),
      ]);
    },
  });
};

export const useSetRachelChatToolApprovalMutation = () => {
  const { team } = useApplicationContext();

  return useMutation({
    mutationFn: async (params: SetRachelChatToolApprovalParams) => {
      const response = await setRachelChatToolApproval(params);
      if (!response.ok) {
        throw response;
      }
    },
    onSuccess: async (_data, { chatId }) => {
      await queryClient.invalidateQueries({
        queryKey: [team?.id, ...getRachelChatKey(chatId)],
      });
    },
  });
};

export const useApproveRachelAgentRunMutation = () => {
  const { team } = useApplicationContext();

  return useMutation({
    mutationFn: async (params: ApproveRachelAgentRunParams) => {
      const response = await approveRachelAgentRun(params);
      if (!response.ok) {
        throw response;
      }
    },
    onSuccess: async (_data, { chatId }) => {
      await queryClient.invalidateQueries({
        queryKey: [team?.id, ...getRachelChatKey(chatId)],
      });
    },
  });
};

export const useSetRachelMessageFeedback = () => {
  const { team } = useApplicationContext();

  return useMutation({
    mutationFn: async (params: SetRachelMessageFeedbackParams) => {
      const response = await setRachelMessageFeedback(params);
      if (!response.ok) {
        throw response;
      }
    },
    onSuccess: async (_data, { chatId }) => {
      await queryClient.invalidateQueries({
        queryKey: [team?.id, ...getRachelChatFeedbackKey(chatId)],
      });
    },
  });
};
